'use client';

import Link from 'next/link';
import { useEffect, useState } from 'react';
import { useOpenSearch } from './DocsShell';
import { docsPath, etaTag, findById, SHOW_ROADMAP } from './data/nav';
import { GLANCE, POPULAR, honestyChips } from './data/leaves';
import { methodChipStyle } from './data/highlight';

/** Docs home — a map, not a pitch. */
export default function DocsHome() {
  const openSearch = useOpenSearch();
  const [kbd, setKbd] = useState('⌘K');
  useEffect(() => {
    if (!/mac/i.test(navigator.platform)) setKbd('Ctrl K');
  }, []);

  const popular = POPULAR.filter(
    (p) => SHOW_ROADMAP || findById(p.id)?.status !== 'roadmap',
  ).map((p) => {
    const item = findById(p.id);
    return {
      ...p,
      tag: item?.status === 'roadmap' ? etaTag(item.eta) : item?.status === 'stub' ? 'STUB' : null,
    };
  });

  return (
    <div className="max-w-[1040px] px-14 pb-24 pt-[60px] max-[720px]:px-6">
      <span className="block font-jetbrains text-[11px] font-semibold tracking-[0.16em]">
        <span className="text-brand-600">DOCS</span>
        <span className="text-dim"> — RII.LINK DEVELOPER DOCUMENTATION</span>
      </span>
      <h1 className="mt-4 font-onest text-[clamp(34px,4.2vw,46px)] font-extrabold leading-[1.08] tracking-[-0.03em]">
        Build with rii<span className="text-brand-600">.link</span>.
      </h1>
      <p className="mt-[18px] max-w-[620px] text-base leading-[1.65] text-muted [text-wrap:pretty]">
        Everything you need to route your first link — and your millionth — in code. This page is a
        map, not a pitch: two quickstarts, the full /v1 reference, and dated stubs wherever a thing
        hasn’t shipped yet.
      </p>

      <button
        type="button"
        onClick={openSearch}
        className="mt-[30px] flex h-[52px] w-full max-w-[560px] cursor-text items-center gap-3 rounded-[14px] border border-line-3 bg-surface px-[18px] text-[14.5px] text-dim shadow-[0_14px_34px_-22px_rgba(16,16,40,.28)] transition-colors hover:border-brand-600"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
          <circle cx="11" cy="11" r="7" />
          <path d="M20 20l-4.2-4.2" />
        </svg>
        <span>Search the docs — endpoints, guides, error codes…</span>
        <span className="ml-auto rounded-md border border-line-2 bg-surface-2 px-2 py-[3px] font-jetbrains text-[10.5px] text-muted">
          {kbd}
        </span>
      </button>

      <div className="mt-11 grid grid-cols-[repeat(auto-fit,minmax(300px,1fr))] gap-4">
        {[
          { tag: 'NO CODE · 3 MIN', t: 'Create a link in the dashboard', d: 'Paste, pick a domain, add a routing rule, see the first click — no API key needed.', id: 'qs-dash' },
          { tag: 'CURL · ~5 MIN', t: 'Create a link over the API', d: 'Create a key in Settings, POST /v1/links, attach a rule, read the clicks back.', id: 'qs-api' },
        ].map((c) => (
          <Link
            key={c.id}
            href={docsPath(c.id)}
            className="flex flex-col gap-[9px] rounded-[var(--radius-ref)] border border-line-2 bg-surface px-[26px] py-6 text-left transition-[border-color,box-shadow] hover:border-brand-600 hover:shadow-[0_14px_34px_-22px_rgba(16,16,40,.28)]"
          >
            <span className="self-start rounded-full border border-line-3 px-[9px] py-[3px] font-jetbrains text-[9.5px] tracking-[0.1em] text-muted">
              {c.tag}
            </span>
            <span className="font-onest text-[17px] font-bold tracking-[-0.01em] text-ink">{c.t}</span>
            <span className="text-[13.5px] leading-[1.6] text-muted">{c.d}</span>
            <span className="mt-1 text-[13px] font-semibold text-brand-600">Open quickstart →</span>
          </Link>
        ))}
      </div>

      <div className="mt-11">
        <span className="font-jetbrains text-[10px] tracking-[0.14em] text-dim">POPULAR PAGES</span>
        <div className="mt-3 flex flex-wrap gap-2">
          {popular.map((p, i) => (
            <Link
              key={`${p.id}-${i}`}
              href={docsPath(p.id)}
              className="inline-flex items-center gap-[7px] rounded-full border border-line-3 bg-surface px-[13px] py-[7px] text-[13px] font-[550] text-ink transition-colors hover:border-brand-600 hover:text-brand-700"
            >
              {p.label}
              {p.tag && (
                <span className="font-jetbrains text-[8.5px] tracking-[0.05em] text-[color-mix(in_srgb,var(--color-data-amber)_62%,var(--color-ink))]">
                  {p.tag}
                </span>
              )}
            </Link>
          ))}
        </div>
      </div>

      <div className="mt-11">
        <span className="font-jetbrains text-[10px] tracking-[0.14em] text-dim">THE API AT A GLANCE</span>
        <div className="mt-3 grid grid-cols-[repeat(auto-fit,minmax(230px,1fr))] gap-3">
          {GLANCE.map((e) => (
            <Link
              key={`${e.m}-${e.p}`}
              href={docsPath(e.id)}
              className="flex flex-col gap-[7px] rounded-xl border border-line-2 bg-surface px-[17px] py-[15px] text-left transition-colors hover:border-brand-600"
            >
              <span className="flex min-w-0 items-center gap-2">
                <span
                  className="flex-none rounded-[5px] border px-[7px] py-0.5 font-jetbrains text-[9.5px] tracking-[0.05em]"
                  style={methodChipStyle(e.m)}
                >
                  {e.m}
                </span>
                <code className="overflow-hidden text-ellipsis whitespace-nowrap font-jetbrains text-[11.5px] text-ink">
                  {e.p}
                </code>
              </span>
              <span className="text-[12.5px] leading-[1.5] text-muted">{e.d}</span>
            </Link>
          ))}
        </div>
      </div>

      {SHOW_ROADMAP && (
        <div className="mt-14 flex flex-wrap items-center gap-2 border-t border-line-2 pt-[18px]">
          <span className="font-jetbrains text-[10px] tracking-[0.12em] text-dim">
            STUBBED, DATED, NOT FAKED —
          </span>
          {honestyChips().map((h) => (
            <Link
              key={h.id}
              href={docsPath(h.id)}
              className="rounded-full border border-[color-mix(in_srgb,var(--color-data-amber)_40%,transparent)] bg-[color-mix(in_srgb,var(--color-data-amber)_12%,white)] px-2.5 py-1 font-jetbrains text-[9.5px] tracking-[0.06em] text-[color-mix(in_srgb,var(--color-data-amber)_62%,var(--color-ink))] transition-colors hover:border-data-amber"
            >
              {h.label}
            </Link>
          ))}
          <span className="font-jetbrains text-[10px] tracking-[0.12em] text-dim">
            — EACH PAGE SAYS WHAT EXISTS TODAY
          </span>
        </div>
      )}
    </div>
  );
}
